// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 11-observability.md
import { z } from 'zod';
import { ClusterTotalsSchema, HealthCrashEntrySchema, HealthInstallationSchema, HealthSnapshotSchema, PLATFORM_SERVICE_KEYS, PlatformServiceSchema, } from './health.js';
import { FleetReleaseValidationPostureSchema } from './fleet-release-validation.js';
import { WorkerTaskSummariesSchema } from './worker-tasks.js';
import { UPDATE_PHASE_STATUSES, UPDATE_PHASES, UPDATE_RUN_MODES, UPDATE_RUN_OUTCOMES, UPDATE_RUN_TRIGGERS, } from './updater-runs.js';
import { CAPACITY_CHANGE_SCHEDULES, CAPACITY_CHANGE_SOURCES, CAPACITY_CHANGE_STATUSES, CapacityValuesSchema, } from './capacity.js';
import { RELEASE_VALIDATION_BUILD_PATTERN, RELEASE_VALIDATION_CASE_ID_MAX, RELEASE_VALIDATION_CASE_OUTCOMES, RELEASE_VALIDATION_CASES_MAX, RELEASE_VALIDATION_OUTCOMES, RELEASE_VALIDATION_STEP_MAX, RELEASE_VALIDATION_SUITES, ReleaseValidationDebugExcerptSchema, } from '../release-validation/vocabulary.js';
/** Each regular fleet report covers one quarter-hour window. */
export const FLEET_REPORT_INTERVAL_MINUTES = 15;
/** An install sends each window's report on its stagger minute, 0 to this minus one, after the window ends. */
export const DELIVERY_STAGGER_WINDOW_MINUTES = 15;
/** Schema version the fleet-monitor ingest currently emits and prefers. */
export const FLEET_REPORT_CURRENT_SCHEMA_VERSION = 19;
/**
 * Oldest schema version `POST /api/v1/reports` accepts. Installs update on
 * their own cadence, not the monitor's, so the floor covers every version
 * still deployed in the fleet — it rises only once the fleet dashboard shows
 * no install emitting below the new floor (docs/27 §API version policy).
 * Every bump since v2 has been additive-with-optional (or tolerated a rename,
 * as v6 does for `release_channel` and v10 does for `bicep_template`), which is
 * what keeps one strict schema able to parse the whole range.
 */
export const FLEET_REPORT_MINIMUM_SCHEMA_VERSION = 2;
/** Versions `POST /api/v1/reports` accepts: the floor through the current. */
export const FLEET_REPORT_ACCEPTED_SCHEMA_VERSIONS = Array.from({ length: FLEET_REPORT_CURRENT_SCHEMA_VERSION - FLEET_REPORT_MINIMUM_SCHEMA_VERSION + 1 }, (_, i) => FLEET_REPORT_CURRENT_SCHEMA_VERSION - i);
/**
 * Field names that must never appear anywhere in an outbound Fleet Health
 * Report (docs/11 §Report allowlist). The schema is strict at every level so
 * any unknown key already fails validation; this list exists so serializer
 * and ingest tests can prove the specific forbidden categories stay out even
 * as the schema evolves.
 */
export const FLEET_REPORT_FORBIDDEN_FIELD_NAMES = [
    'logs',
    'log_lines',
    'raw_logs',
    'stack_trace',
    'stacktrace',
    'request_path',
    'request_paths',
    'query_text',
    'query',
    'prompt',
    'prompt_body',
    'response_body',
    'request_body',
    'source_code',
    'env',
    'env_vars',
    'environment_variables',
    'secret',
    'secrets',
    'api_key',
    'password',
    'token',
    'credentials',
    'connection_string',
    'user_email',
    'email',
    'emails',
    'idp_subject',
    // Identity fields travel once at enrollment, never in the recurring report.
    'org_name',
];
// The outbound crash entry is exactly the snapshot's crash entry (docs/11
// `crashes[]`) — one shared shape, no drift between the local snapshot and the
// forwarded report. `.strict()` is redundant with the report's deepStrict pass
// below but keeps this export usable on its own.
export const FleetCrashEntrySchema = HealthCrashEntrySchema.strict();
/**
 * UTC days of usage each report re-states. Every day carries its **absolute**
 * total rather than the window's delta, so the fleet monitor stores days
 * last-writer-wins: a duplicate delivery is a no-op and an install offline for
 * up to `FLEET_USAGE_LOOKBACK_DAYS - 1` days backfills fully on reconnect. A
 * delta design would instead lose a dropped window's counts permanently.
 */
export const FLEET_USAGE_LOOKBACK_DAYS = 3;
/** Trailing window for the active-user gauge; inside `app_usage_presence` retention. */
export const FLEET_USAGE_ACTIVE_WINDOW_DAYS = 28;
const usageCount = () => z.number().int().nonnegative();
/**
 * One app's distinct active users over the trailing window — the `mau_per_app`
 * billable grain (ADR-0056). A gauge, restated in full by every report; one
 * person using three apps contributes to three entries.
 */
export const FleetUsageMauPerAppSchema = z.object({
    app_id: z.string().uuid(),
    active_users_28d: usageCount(),
});
/**
 * One UTC day of aggregate usage. Every field is `null` only when its own
 * source read was unavailable — never as a stand-in for zero, which is a real
 * measurement the pricing model must be able to tell apart from no data.
 * `audit_events` and `proxy_calls` share the `audit_log` read; `app_requests`
 * and `active_users_1d` share the usage-rollup read; `deployments` has its own,
 * so an audit-volume failure does not null it.
 */
export const FleetUsageDaySchema = z.object({
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    audit_events: z
        .object({
        total: usageCount(),
        // Keyed by audit category (docs/11 §What is logged). A free record rather
        // than a fixed enum so an install on a newer release can report a category
        // this reader predates without failing the strict schema.
        by_category: z.record(usageCount()),
    })
        .nullable(),
    proxy_calls: z.object({ succeeded: usageCount(), failed: usageCount() }).nullable(),
    app_requests: usageCount().nullable(),
    active_users_1d: usageCount().nullable(),
    // Optional, unlike its siblings: a report from the previous schema version
    // carries days without it, and those days must still parse.
    deployments: z.object({ succeeded: usageCount(), failed: usageCount() }).nullable().optional(),
});
/**
 * Aggregate usage for pricing analysis (docs/11 §Fleet Health Reporting).
 * Counts only — no identities, paths, or payloads.
 *
 * `days[]` holds summable per-day counters. `active_users_28d` is a **gauge**:
 * a trailing-window distinct count as of this report, so it is never summed
 * across reports or days. Summing per-day `active_users_1d` over a month would
 * count a daily-active user once per day, which is why no summed active-user
 * field exists anywhere in this contract. `mau_per_app` is a gauge for the same
 * reason, per app.
 */
export const FleetUsageSchema = z.object({
    window_start: z.string().datetime({ offset: true }),
    window_end: z.string().datetime({ offset: true }),
    active_users_28d: usageCount().nullable(),
    /**
     * Each app's distinct active users over the same trailing window, sorted by
     * `app_id` — the quantity `mau_per_app` bills (ADR-0056). One entry per app,
     * not per app per day: the billable figure is the window gauge, so per-day
     * rows would multiply the payload by the lookback for a number nothing reads.
     *
     * Apps with no active users in the window are omitted, so an empty array is a
     * measured "nobody used anything" and `null` is a failed read. Summing these
     * gives user-apps, which is deliberately larger than `active_users_28d`: that
     * stays the install's distinct people, and the two never reconcile.
     *
     * Optional so a v4 report mid-upgrade still parses.
     */
    mau_per_app: z.array(FleetUsageMauPerAppSchema).nullable().optional(),
    days: z.array(FleetUsageDaySchema),
});
// Record keys in the snapshot are identifiers (task kinds, app statuses,
// platform resource roles) — never free text. Bounding them closes the one
// door strict objects leave open: arbitrary strings arriving as map keys.
const RECORD_KEY_PATTERN = /^[a-z][a-z0-9._-]{0,63}$/;
/**
 * Whether a string may be used as a record key in a report. Grammar *and*
 * forbidden-name check, because both reject and the caller cares about neither
 * distinction: a key that fails either one fails the whole strict parse, which
 * costs the install its report rather than the single entry.
 *
 * Exported so emitters screen a key before it reaches the payload, and shared
 * with `recordKeySchema` below so the screen and the parse cannot drift.
 */
export function isFleetReportRecordKey(key) {
    return RECORD_KEY_PATTERN.test(key) && !FLEET_REPORT_FORBIDDEN_FIELD_NAMES.includes(key);
}
const recordKeySchema = z.string().refine(isFleetReportRecordKey, {
    message: 'not a permitted record key',
});
/**
 * Recursively re-creates a schema with `.strict()` applied to every object
 * level (so unknown nested fields fail validation instead of being stripped)
 * and bounded keys on every record. Covers the composite types
 * HealthSnapshotSchema actually uses; anything else (string/number/enum/…)
 * passes through unchanged.
 */
function deepStrict(schema) {
    if (schema instanceof z.ZodObject) {
        const shape = {};
        for (const [key, value] of Object.entries(schema.shape)) {
            shape[key] = deepStrict(value);
        }
        return z.object(shape).strict();
    }
    if (schema instanceof z.ZodArray) {
        // Rebuilding the array drops the element's own length checks with it, so a
        // documented cap (`updater_runs`, the phase list) would silently stop being
        // enforced. Carry them across rather than re-declaring them at the caller.
        const { minLength, maxLength, exactLength } = schema._def;
        let rebuilt = z.array(deepStrict(schema.element));
        if (minLength !== null)
            rebuilt = rebuilt.min(minLength.value);
        if (maxLength !== null)
            rebuilt = rebuilt.max(maxLength.value);
        if (exactLength !== null)
            rebuilt = rebuilt.length(exactLength.value);
        return rebuilt;
    }
    if (schema instanceof z.ZodRecord) {
        return z.record(recordKeySchema, deepStrict(schema.valueSchema));
    }
    if (schema instanceof z.ZodOptional)
        return deepStrict(schema.unwrap()).optional();
    if (schema instanceof z.ZodNullable)
        return deepStrict(schema.unwrap()).nullable();
    // A union's branches are objects too. Without this the branch schemas keep
    // Zod's default strip behaviour, so an unknown key inside `provider_iac_package`
    // would be silently dropped instead of failing the parse — the one thing the
    // outbound allowlist exists to prevent.
    if (schema instanceof z.ZodDiscriminatedUnion) {
        return z.discriminatedUnion(schema.discriminator, schema.options.map((option) => deepStrict(option)));
    }
    return schema;
}
// The report body mirrors HealthSnapshotSchema minus the local-only fields —
// `trends` is a dashboard read-model extension, `cluster_nodes` is the
// install's own per-node view, and `collected_at` is per-snapshot, while a
// report covers a window — plus the `crashes` summary and
// the `usage` section the forwarder builds over the window (docs/27 §API).
// `usage` is optional: it is computed at forward time rather than collected
// into the snapshot, and v2 installs mid-upgrade report without it.
/**
 * A pipeline step that failed in the delivery window: which check, on which app,
 * and when. Report-only, like `usage` — it is a per-window aggregate rather than
 * a property of one snapshot.
 *
 * Deliberately **not** merged into `crashes`, which is Kubernetes container
 * terminations. A scan refusing a commit and a container running out of memory
 * are different events with different remedies, and one list would make the
 * fleet unable to tell an operator which it was looking at.
 *
 * Four fields and no more. A failing check's *output* is the tempting thing to
 * send and the one thing that cannot leave the install: logs, diffs, rule
 * matches, and commit messages all carry customer source. The check name is a
 * bounded identifier (`gitleaks`, `semgrep`, `trivy`, `opa`, `deploy`) and the
 * report allowlist rejects the rest.
 */
export const FleetPipelineFailureSchema = z.object({
    app_slug: z.string(),
    check_name: z.string().max(64),
    occurred_at: z.string().datetime({ offset: true }),
    /** How many times this app/check pair failed in the window. */
    count: z.number().int().positive(),
});
/**
 * Runs one report carries. Five is the window a fleet reader triages from:
 * enough to see a nightly check settle into a pattern, small enough that the
 * section stays a footnote of the payload at the 15-minute cadence. On a
 * schedule short enough to outpace it the fleet's history has gaps by design
 * (docs/11 §Updater runs section); the install stays the complete record.
 */
export const FLEET_UPDATER_RUNS_MAX = 5;
/**
 * Phase entries one run may carry. The updater has five phases today; the cap
 * leaves headroom so an install on a release that added one is bounded rather
 * than rejected, for the same reason its name is tolerated below.
 */
export const FLEET_UPDATER_RUN_PHASES_MAX = 12;
/** Matches `update_runs.target_version` / `previous_version`. */
const UPDATER_VERSION_MAX = 64;
/**
 * A snake_case token: the shape every value in the updater's vocabularies and
 * every code in its reason list already has.
 */
const VOCABULARY_TOKEN = '[a-z][a-z0-9_]{0,39}';
/**
 * The grammar `update_runs.reason` and a phase's `details.reason` must match to
 * cross: one code, optionally qualified by one slug (`post_apply_check_failed:dns_resolves`).
 *
 * A length bound is not enough here. `reason` is the one string in the section
 * promoted out of a `jsonb` column another component writes under its own role,
 * typed plain `string` at the writer (`FinishUpdateRunInput`), so the closed
 * vocabulary docs/11 and docs/27 both promise is enforced by author discipline
 * in another app and by nothing at the boundary that exists to be the guard.
 * The same argument that keeps the rest of `details` out — no check can tell a
 * result code from a hostname a future code interpolated into one — applies to
 * a free-text `reason`, so the grammar is what makes the promise checkable.
 *
 * Exported so the install-side collector screens a reason with the same rule
 * that parses it, and drops a non-conforming one to `null` rather than letting
 * a malformed row cost the whole report.
 */
export const FLEET_UPDATER_REASON_PATTERN = new RegExp(`^${VOCABULARY_TOKEN}(:${VOCABULARY_TOKEN})?$`);
const updaterReason = () => z.string().regex(FLEET_UPDATER_REASON_PATTERN).nullable();
/**
 * A vocabulary the sending install may already have extended. The known values
 * are imported from the run store so the two cannot drift, and an unknown value
 * of the same shape still parses.
 *
 * Adding a value to one of these enums is not a field addition, so nothing in
 * docs/27's additive-within-a-version rule forces a `schema_version` bump. A
 * strict enum would then answer a report naming a phase or an outcome this
 * reader predates with `reports.payload_rejected`, which the delivery contract
 * treats as non-retryable — costing the entire report and the window's billing
 * input over one unrecognized word. Same choice, and the same reasoning, as
 * `by_category` above; the monitor stores the value verbatim and renders an
 * unrecognized one as such.
 */
const openVocabulary = (known) => z.union([z.enum(known), z.string().regex(new RegExp(`^${VOCABULARY_TOKEN}$`))]);
/**
 * One `(run_id, phase)` result: which phase, how it ended, how long it took,
 * and the bounded code it recorded.
 *
 * The phase's remaining `details` codes deliberately do not cross, on privacy
 * grounds rather than mechanical ones. `details` is a
 * `Record<string, string | number | boolean>` whose "secret-free result codes"
 * contract is a comment, not a type, and it is authored by the updater under its
 * own credentials. No check can tell a result code from a hostname a future code
 * interpolated into one, so the open value space is declined outright. A detail
 * code worth reading is worth promoting to the run's own `reason`, a closed
 * vocabulary, which is where `post_apply_check_failed:<slug>` already lands.
 */
export const FleetUpdaterRunPhaseSchema = z.object({
    phase: openVocabulary(UPDATE_PHASES),
    status: openVocabulary(UPDATE_PHASE_STATUSES),
    /** Null while the phase is still open: there is no duration to report yet. */
    duration_ms: z.number().int().nonnegative().nullable(),
    reason: updaterReason(),
});
/**
 * One updater run, summarized: what it was doing, what came of it, and how each
 * phase went. Report-only like `usage`, built at forward time from the
 * install's own run store rather than collected into a snapshot.
 *
 * The vocabulary is imported from the run store's, not restated, so an outcome
 * the updater can record is an outcome the fleet can read. That matters most for
 * the two the fleet would otherwise misjudge: `applied` stays `applied` when a
 * closing check failed after the release landed, and a run the control plane
 * synthesized because a Job never reported crosses as `unreported` rather than
 * as a failure the updater never claimed. A null `outcome` is a run still in
 * flight at report time.
 *
 * **No transcript, plan, or command output crosses in any field.** Every string
 * here is a bounded identifier or a result code the updater's writer sanitized;
 * the transcript stays on the install, one Internal Health download away
 * (docs/26 §Run transcripts).
 */
export const FleetUpdaterRunSchema = z.object({
    run_id: z.string().uuid(),
    mode: openVocabulary(UPDATE_RUN_MODES),
    trigger: openVocabulary(UPDATE_RUN_TRIGGERS),
    target_version: z.string().max(UPDATER_VERSION_MAX).nullable(),
    previous_version: z.string().max(UPDATER_VERSION_MAX).nullable(),
    started_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullable(),
    outcome: openVocabulary(UPDATE_RUN_OUTCOMES).nullable(),
    reason: updaterReason(),
    /**
     * At most one entry per phase: the rows are keyed `(run_id, phase)`. The cap
     * is the known phase count plus room for phases a newer install has that this
     * reader does not, on the same reasoning as `openVocabulary`.
     */
    phases: z.array(FleetUpdaterRunPhaseSchema).max(FLEET_UPDATER_RUN_PHASES_MAX),
});
/** Newest `(build, suite)` verdicts one report carries; with the section byte cap this bounds the payload. */
export const FLEET_RELEASE_VALIDATION_RUNS_MAX = 6;
/** One case in a verdict. `outcome` is open vocabulary like the run's own. */
export const FleetReleaseValidationCaseSchema = z.object({
    id: z.string().min(1).max(RELEASE_VALIDATION_CASE_ID_MAX),
    outcome: openVocabulary(RELEASE_VALIDATION_CASE_OUTCOMES),
    duration_ms: z.number().int().nonnegative().nullable(),
    step: z.string().max(RELEASE_VALIDATION_STEP_MAX).nullable(),
});
/**
 * One suite's verdict on one build the install validated. `suite` and
 * `outcome` are open vocabulary (same reasoning as `FleetUpdaterRunSchema`): a
 * newer install may add a value, and rejecting it would cost the whole report.
 * A still-running suite crosses with `outcome: 'in_flight'` and
 * `finished_at: null`. The transcript, the Job name, and any screenshot or
 * trace never cross; `debug_excerpt` is bounded text.
 */
export const FleetReleaseValidationRunSchema = z.object({
    build: z.string().regex(RELEASE_VALIDATION_BUILD_PATTERN),
    suite: openVocabulary(RELEASE_VALIDATION_SUITES),
    outcome: openVocabulary(RELEASE_VALIDATION_OUTCOMES),
    started_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullable(),
    cases: z.array(FleetReleaseValidationCaseSchema).max(RELEASE_VALIDATION_CASES_MAX),
    failing_case: z.string().max(RELEASE_VALIDATION_CASE_ID_MAX).nullable(),
    failing_step: z.string().max(RELEASE_VALIDATION_STEP_MAX).nullable(),
    reason: updaterReason(),
    debug_excerpt: ReleaseValidationDebugExcerptSchema.nullable(),
});
/** Recent capacity changes carried per report; the newest first. */
export const FLEET_CAPACITY_CHANGES_MAX = 10;
/**
 * One capacity change: what was asked for, against what, how it was asked
 * (`origin`: a manual change, an approved ask, or an accepted suggestion), and
 * how it ended. Counts and bounded codes only; who asked stays on the install.
 */
export const FleetCapacityChangeSchema = z.object({
    change_id: z.string().uuid(),
    status: openVocabulary(CAPACITY_CHANGE_STATUSES),
    origin: openVocabulary(CAPACITY_CHANGE_SOURCES),
    schedule: openVocabulary(CAPACITY_CHANGE_SCHEDULES),
    requested: CapacityValuesSchema.partial(),
    previous: CapacityValuesSchema,
    created_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullable(),
    reason: updaterReason(),
});
/**
 * The install's node counts, any ask waiting at the ceiling or shrink
 * suggestion, and its recent changes (docs/46 §Topology is uniform, size is
 * per install): the fleet sees every automatic change and every pending ask.
 */
export const FleetCapacitySchema = z.object({
    effective: CapacityValuesSchema.nullable(),
    ask: z
        .object({
        current: z.number().int(),
        proposed: z.number().int(),
        monthly_cost_delta_usd: z.number().nullable(),
    })
        .nullable(),
    suggestion: z
        .object({
        current: z.number().int(),
        proposed: z.number().int(),
        monthly_saving_usd: z.number().nullable(),
    })
        .nullable(),
    changes: z.array(FleetCapacityChangeSchema).max(FLEET_CAPACITY_CHANGES_MAX),
});
/**
 * Replicas one report carries per platform service. The ready and desired
 * counts stay whole, so a trimmed list never reads as fewer replicas.
 */
export const FLEET_PLATFORM_SERVICE_REPLICAS_MAX = 5;
export const FleetPlatformServiceSchema = PlatformServiceSchema.extend({
    replicas: PlatformServiceSchema.shape.replicas.max(FLEET_PLATFORM_SERVICE_REPLICAS_MAX),
});
/** The web access the install serves (docs/11 §Network posture). */
export const FleetIngressProfileSchema = z.enum(['public', 'private']);
/**
 * Whether the install's egress leaves through the customer's firewall. Never
 * the firewall's address; `null` when the install could not read its install
 * inputs, which is unknown rather than direct.
 */
export const FleetEgressSchema = z.enum(['direct', 'next_hop']).nullable();
const reportShape = HealthSnapshotSchema.omit({
    trends: true,
    collected_at: true,
    cluster_nodes: true,
}).extend({
    // An install on a pre-v6 version still names its update posture the retired
    // way, and the strict ingest would reject the whole report over that one key.
    // Both spellings are optional while pre-v6 versions sit above the acceptance
    // floor (docs/27 §API version policy); `release_channel` comes out when the
    // floor passes v5. Built from the plain installation object rather than the
    // snapshot's adapted one, so deepStrict below still sees an object to make
    // strict.
    installation: HealthInstallationSchema.extend({
        include_prereleases: z.boolean().nullable().optional(),
        release_channel: z.string().optional(),
    }),
    crashes: z.array(FleetCrashEntrySchema),
    usage: FleetUsageSchema.optional(),
    pipeline_failures: z.array(FleetPipelineFailureSchema).optional(),
    // Absent on an install one report version behind, and on one whose run store
    // predates run records entirely. Absence therefore means "has not reported
    // runs", which stays distinct from a reported empty list: the answer that says
    // the updater has run nothing at all.
    updater_runs: z.array(FleetUpdaterRunSchema).max(FLEET_UPDATER_RUNS_MAX).optional(),
    // Absent below v13. Optional rather than defaulted so the monitor can tell an
    // install that has not said from one that said no.
    release_validation: FleetReleaseValidationPostureSchema.optional(),
    // Absent below v14, and whenever the install could not read its capacity.
    capacity: FleetCapacitySchema.optional(),
    // Absent below v15.
    ingress_profile: FleetIngressProfileSchema.optional(),
    egress: FleetEgressSchema.optional(),
    // Absent below v16, and on an install that holds no validation rows. Absent
    // rather than empty: an install that has validated nothing has said nothing.
    release_validation_runs: z
        .array(FleetReleaseValidationRunSchema)
        .max(FLEET_RELEASE_VALIDATION_RUNS_MAX)
        .optional(),
    // Absent below v17; null when the install could not read its cluster.
    platform_services: z
        .array(FleetPlatformServiceSchema)
        .max(PLATFORM_SERVICE_KEYS.length)
        .nullable()
        .optional(),
    // Absent below v19, and whenever the install has no per-node view to sum.
    cluster_totals: ClusterTotalsSchema.optional(),
});
// deepStrict only toggles unknown-key handling, so the inferred type is
// unchanged; the cast restores the static type the runtime transform keeps.
export const FleetHealthReportSchema = deepStrict(reportShape);
export const FLEET_MINIMAL_REPORT_SHAPE = 'minimal';
/**
 * What a forwarder window with no health snapshot sends (docs/27 §The minimal
 * report): the window and the scheduled-task summary, so an install whose
 * collector is failing reads as degraded rather than silent. It carries no
 * billing, usage or snapshot section, and only a full report counts as a
 * delivery for fleet-reporting enforcement.
 */
export const FleetMinimalReportSchema = z
    .object({
    shape: z.literal(FLEET_MINIMAL_REPORT_SHAPE),
    schema_version: z.number().int(),
    generated_at: z.string().datetime({ offset: true }),
    installation_id: z.string().uuid(),
    window_start: z.string().datetime({ offset: true }),
    window_end: z.string().datetime({ offset: true }),
    worker_tasks: WorkerTaskSummariesSchema,
})
    .strict();
/** True for a body that declares itself the minimal shape; the full report has no `shape`. */
export function isMinimalReportBody(body) {
    return (typeof body === 'object' &&
        body !== null &&
        body.shape === FLEET_MINIMAL_REPORT_SHAPE);
}
export const FleetReportDeliveryStatusSchema = z.enum([
    'pending',
    'sent',
    'failed_retryable',
    'failed_permanent',
]);
export const FleetEnrollmentStatusSchema = z.enum(['pending', 'enrolled', 'failed']);
// API projection of the install's `fleet_enrollments` row for /internal-health
// (docs/11 §Fleet Health Reporting): enrollment state plus the exact identity
// payload sent to the fleet monitor, so the customer sees precisely what
// identifies their install (installation id, key hash, org name, Entra tenant
// id, region, billing origin — nothing else).
export const FleetEnrollmentSchema = z.object({
    status: FleetEnrollmentStatusSchema,
    enrollment_payload: z.record(z.unknown()),
    attempt_count: z.number().int(),
    last_attempt_at: z.string().datetime({ offset: true }).nullable(),
    next_attempt_at: z.string().datetime({ offset: true }).nullable(),
    enrolled_at: z.string().datetime({ offset: true }).nullable(),
    response_status: z.number().int().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
});
export const FleetKeyRotationStatusSchema = z.enum(['pending', 'rotated', 'failed']);
// API projection of the install's `fleet_key_rotations` row for
// /internal-health: the state of the latest install-initiated reporting-key
// rotation cycle, so a rotation that keeps failing is visible while report
// deliveries continue on the old key. No key material — hashes stay server-side.
export const FleetKeyRotationSchema = z.object({
    status: FleetKeyRotationStatusSchema,
    attempt_count: z.number().int(),
    last_attempt_at: z.string().datetime({ offset: true }).nullable(),
    next_attempt_at: z.string().datetime({ offset: true }).nullable(),
    rotated_at: z.string().datetime({ offset: true }).nullable(),
    response_status: z.number().int().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
});
export const FleetContactDeliveryStatusSchema = z.enum(['pending', 'sent', 'failed']);
// API projection of the install's `fleet_contact_deliveries` row for
// /internal-health (docs/27 §`POST /api/v1/contact`): the state of forwarding
// the install contact, plus the exact payload sent — nothing leaves the
// boundary that IT cannot inspect. The payload is read from the org row rather
// than the ledger, which fingerprints the pair instead of copying it — so the
// status describes this payload only when the fingerprints agree, and a pair
// corrected since the last attempt reads as `pending`.
export const FleetContactDeliverySchema = z.object({
    status: FleetContactDeliveryStatusSchema,
    contact_payload: z.object({ contact_name: z.string(), contact_email: z.string() }),
    attempt_count: z.number().int(),
    last_attempt_at: z.string().datetime({ offset: true }).nullable(),
    next_attempt_at: z.string().datetime({ offset: true }).nullable(),
    sent_at: z.string().datetime({ offset: true }).nullable(),
    response_status: z.number().int().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
});
export const FleetReportingStateSchema = z.enum(['ok', 'warning', 'degraded']);
/**
 * Install-level fleet-reporting enforcement state (docs/11 §Fleet reporting
 * enforcement). `warning` surfaces the "building will be disabled on
 * `degrade_at`" banner; `degraded` closes the new-work surfaces. `degrade_at`
 * is null only while there is no signal to measure yet — a fresh install whose
 * first report window has not been recorded.
 */
export const FleetReportingEnforcementSchema = z.object({
    state: FleetReportingStateSchema,
    state_since: z.string().datetime({ offset: true }),
    last_successful_delivery_at: z.string().datetime({ offset: true }).nullable(),
    degrade_at: z.string().datetime({ offset: true }).nullable(),
});
// API projection of a `fleet_report_deliveries` row for /internal-health
// (docs/11 AC-OBS-44): delivery status plus the exact JSON payload sent (or
// queued). The payload is a plain record, not FleetHealthReportSchema — the
// page must render precisely what a historical row carried even if the report
// schema has since moved on.
export const FleetReportDeliverySchema = z.object({
    id: z.string(),
    status: FleetReportDeliveryStatusSchema,
    window_start: z.string().datetime({ offset: true }),
    window_end: z.string().datetime({ offset: true }),
    attempt_count: z.number().int(),
    last_attempt_at: z.string().datetime({ offset: true }).nullable(),
    next_attempt_at: z.string().datetime({ offset: true }).nullable(),
    sent_at: z.string().datetime({ offset: true }).nullable(),
    response_status: z.number().int().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
    report_payload: z.record(z.unknown()),
});
//# sourceMappingURL=fleet-report.js.map